// ChatBubble Component
// ====================
// Renders a single chat message bubble for the Anna onboarding conversation flow.
// Supports three sender types: anna (left-aligned), user (right-aligned), system (centered).

import { ReactNode } from 'react';

interface ChatBubbleProps {
  sender: 'anna' | 'user' | 'system';
  children: ReactNode;
  visible?: boolean;
  className?: string;
}

export function ChatBubble({ sender, children, visible = true, className = '' }: ChatBubbleProps) {
  if (!visible) return null;

  const baseStyles = 'rounded-2xl px-4 py-3 max-w-[85%] transition-all duration-300';

  const senderStyles: Record<string, string> = {
    anna: `${baseStyles} bg-white/70 backdrop-blur-md border border-white/50 self-start rounded-bl-sm text-gray-700`,
    user: `${baseStyles} bg-orange-50/80 border border-orange-100 self-end rounded-br-sm text-gray-800`,
    system: `${baseStyles} bg-gray-50/50 self-center text-center text-gray-500 text-sm`,
  };

  const wrapperAlignment: Record<string, string> = {
    anna: 'flex justify-start',
    user: 'flex justify-end',
    system: 'flex justify-center',
  };

  return (
    <div
      className={`${wrapperAlignment[sender]} ${className}`}
      style={{
        animation: 'bubbleIn 0.3s ease-out forwards',
        opacity: 0,
      }}
    >
      <div className={senderStyles[sender]}>
        {children}
      </div>
      <style>{`
        @keyframes bubbleIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
